// Schedule templates.
//
// These used to emit FLAT rows on PINNED dates: six activities, no
// indentation, no relationships, each one nailed to "anchor + N weeks".
// That is the opposite of what the product is trying to teach - a plan
// where nothing follows anything cannot move when the job moves, and
// every date is a guess someone has to re-type.
//
// A template now builds a NETWORK:
//
//   ref          a handle other rows point at, unique within the build
//   indent       0..n - a row with deeper rows under it is a summary
//   durationDays working days (0 = a milestone)
//   after        [{ ref, type, lagDays }] - the same four relationships
//                the engine understands, by ref rather than row number
//   category / stage / kind   the two axes from step 2
//
// Only the FIRST row carries a date. Everything else is scheduled by the
// engine from its links, so dragging the start of a template moves the
// whole sequence, and stretching one activity pushes what follows it.
//
// Templates are AREA-PARAMETERISED: `build({ anchor, areas })` repeats
// its chain once per area given, under a summary per area. With no areas
// it builds the chain once, unparameterised.
//
// The type vocabulary moved to helpers/schedule/activityTypes when `type`
// was split into behaviour + kind; re-exported here so the existing
// importers keep working.
export { ACTIVITY_TYPES, ZERO_DURATION_TYPES } from "./activityTypes";

// Local yyyy-mm-dd, `weeks` weeks after the anchor date (today when empty).
export const isoWeeksFrom = (anchorIso, weeks) => {
  const d = anchorIso ? new Date(`${anchorIso}T00:00:00`) : new Date();
  d.setDate(d.getDate() + weeks * 7);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const row = (patch) => ({
  ref: "", name: "", type: "task", kind: "", category: "", stage: "",
  indent: 0, durationDays: 1, after: [], level: "", discipline: "",
  ...patch,
});
const ms = (patch) => row({ type: "finish_ms", durationDays: 0, ...patch });

// A chain: each step follows the one before it, finish-to-start. This is
// the shape most construction sequences actually have, and writing it
// out link by link in every template is how templates end up unlinked.
const chain = (prefix, steps, common = {}) =>
  steps.map((st, i) =>
    row({
      ...common,
      ...st,
      ref: `${prefix}-${st.ref || i}`,
      after: i === 0
        ? (st.after || [])
        : [{ ref: `${prefix}-${steps[i - 1].ref || i - 1}`, type: "FS", lagDays: 0 }, ...(st.after || [])],
    })
  );

// Repeat a builder once per area, each under its own summary. The areas
// run in parallel by default - they are different parts of the building,
// and a template that serialises them is inventing a constraint that is
// not there.
const perArea = (areas, prefix, summaryName, make) => {
  const list = (areas && areas.length ? areas : [null]);
  const out = [];
  list.forEach((area, i) => {
    const tag = area ? (area.key || area.name || `a${i}`) : `a${i}`;
    const p = `${prefix}-${String(tag).replace(/\s+/g, "_")}`;
    if (area) {
      out.push(row({
        ref: `${p}-sum`, name: summaryName(area), indent: 0,
        category: "management", stage: "",
      }));
    }
    for (const r of make(p, area)) {
      out.push({ ...r, indent: (area ? 1 : 0) + (r.indent || 0), level: r.level || (area ? area.level || "" : "") });
    }
  });
  return out;
};

// ── the WBS backbone ──────────────────────────────────────────────────
// The 16 branches every schedule on this system starts from, as summary
// rows. Deliberately has no durations and no links: it is the filing
// cabinet, and the work goes inside it.
//
// This list is the FALLBACK. Every company owns an editable `wbs_node`
// list (back/utils/defaultTaxonomy, seeded on company creation), and the
// template reads that when the page hands it over - otherwise a company
// that renamed "09 Electrical" or added a branch would still be handed
// the stock sixteen, which makes its own vocabulary a lie.
const WBS_BACKBONE = [
  ["01", "Project Management", "management", ""],
  ["02", "Engineering / BIM", "modeling", "preconstruction"],
  ["03", "Procurement", "procurement", "preconstruction"],
  ["04", "Site / Civil", "install", "site_civil"],
  ["05", "Foundations", "install", "foundations"],
  ["06", "Structure", "install", "structure"],
  ["07", "Building Envelope", "install", "envelope"],
  ["08", "Mechanical", "install", "mep_rough_in"],
  ["09", "Electrical", "install", "mep_rough_in"],
  ["10", "Plumbing", "install", "mep_rough_in"],
  ["11", "Fire Protection", "install", "mep_rough_in"],
  ["12", "Architectural", "install", "interiors"],
  ["13", "Low Voltage / Controls", "install", "controls"],
  ["14", "Equipment", "equipment_set", "equipment"],
  ["15", "Testing & Commissioning", "commissioning", "commissioning"],
  ["16", "Closeout / Turnover", "closeout", "closeout"],
];

export const SCHEDULE_TEMPLATES = [
  // THE PROJECT'S OWN STRUCTURE, BUILT ON THE SERVER (owner, 2026-10-15:
  // "make this a default build from project spatial/system button on the
  // schedule template"). Not rows for the draft: BIM > Modeling /
  // Coordination / Shop drawings and Field > Install, then every level,
  // area and system of Levels, Areas & Systems, written to the saved
  // schedule after a readiness check and a preview
  // (components/Coordination/SystemTasksBuilder, back/utils/systemTasks).
  {
    key: "levels_areas_systems",
    name: "Build from project levels, areas & systems",
    description:
      "BIM (Modeling, Coordination, Shop drawings) and Field (Install), each with every level, area and system from Levels, Areas & Systems. Rows already on the schedule are kept and reused; each system gets an equal share of its area's working days, in order. You see a preview before anything is saved.",
    areaAware: false,
    serverBuild: true,
    build: () => [],
  },
  {
    key: "backbone",
    name: "WBS backbone",
    description:
      "Your company's WBS branches as summary rows to hang work under — edited in the company vocabularies, not hardcoded here. No dates and no links: a summary takes its dates from its children.",
    areaAware: false,
    build: ({ wbsNodes } = {}) => {
      // A company node carries no activity category of its own, so the
      // stock list is used to fill one in where the code still matches -
      // a renamed branch keeps its meaning, a brand-new one simply has
      // no category rather than a guessed one.
      const categoryByCode = new Map(WBS_BACKBONE.map(([code, , category]) => [code, category]));
      const source = (wbsNodes && wbsNodes.length)
        ? wbsNodes.map((n) => [
            String(n.code || ""),
            n.label || n.key || "Branch",
            categoryByCode.get(String(n.code || "")) || "",
            n.stage || "",
          ])
        : WBS_BACKBONE;
      return source.map(([code, label, category, stage], i) =>
        row({
          ref: `wbs-${code || i}`,
          name: `${code ? `${code} ` : ""}${label}`,
          type: "summary", durationDays: 0, category, stage,
        })
      );
    },
  },

  {
    key: "coordination_area",
    name: "Coordination → area release (per area)",
    description:
      "The VDC sequence that actually gates the field: model, clash, sign-off, IFF, then the area release the trades wait on. Repeats per area you pick.",
    areaAware: true,
    build: ({ areas } = {}) => perArea(areas, "coord", (a) => `Coordination — ${a.name}`, (p, area) =>
      chain(p, [
        { ref: "model", name: "Trade modelling", durationDays: 15, category: "modeling", stage: "preconstruction" },
        { ref: "clash", name: "Clash detection & resolution", durationDays: 10, category: "coordination", stage: "preconstruction" },
        { ref: "signoff", name: "Coordination sign-off", type: "finish_ms", durationDays: 0, kind: "signoff", category: "coordination", stage: "preconstruction" },
        { ref: "iff", name: "Issue for fabrication (IFF)", type: "finish_ms", durationDays: 0, kind: "iff", category: "drawings", stage: "preconstruction" },
        { ref: "release", name: `Area release${area ? ` — ${area.name}` : ""}`, type: "start_ms", durationDays: 0, kind: "area_release", category: "coordination", stage: "mep_rough_in" },
      ])
    ),
  },

  {
    key: "electrical_room",
    name: "Electrical room chain (per area)",
    description:
      "Rough-in through energisation for an electrical room: conduit, gear set, terminations, inspection, then energised. Every step follows the one before it.",
    areaAware: true,
    build: ({ areas } = {}) => perArea(areas, "elec", (a) => `Electrical room — ${a.name}`, (p) =>
      chain(p, [
        { ref: "layout", name: "Layout & hangers", durationDays: 3, category: "layout" },
        { ref: "conduit", name: "Conduit & rough-in", durationDays: 10, category: "install" },
        { ref: "gear", name: "Set gear / switchboard", durationDays: 4, category: "equipment_set", stage: "equipment" },
        { ref: "feeders", name: "Pull feeders", durationDays: 6, category: "install" },
        { ref: "term", name: "Terminations", durationDays: 5, category: "install" },
        { ref: "insp", name: "AHJ inspection", durationDays: 1, category: "inspection", stage: "commissioning" },
        { ref: "energize", name: "Energisation", type: "finish_ms", durationDays: 0, kind: "energization", category: "energization", stage: "commissioning" },
      ], { discipline: "Electrical", stage: "mep_rough_in" })
    ),
  },

  {
    key: "procurement",
    name: "Procurement chain (long-lead equipment)",
    description:
      "Submittal to delivery for one long-lead item, with the fabrication lead as an actual duration rather than a date somebody remembered.",
    areaAware: false,
    build: () => chain("proc", [
      { ref: "submittal", name: "Submittal prepared", durationDays: 5, category: "submittal" },
      { ref: "review", name: "Engineer review & approval", durationDays: 10, category: "submittal" },
      { ref: "release", name: "Release for fabrication", type: "finish_ms", durationDays: 0, kind: "design_release", category: "procurement" },
      { ref: "fab", name: "Fabrication lead time", durationDays: 60, category: "prefab" },
      { ref: "ship", name: "Shipping & delivery", durationDays: 10, category: "delivery" },
      { ref: "onsite", name: "On site", type: "finish_ms", durationDays: 0, kind: "delivery", category: "delivery" },
    ], { stage: "preconstruction" }),
  },

  {
    key: "commissioning",
    name: "Commissioning chain",
    description:
      "Pre-functional through turnover, including the integrated system test. The steps overlap where they really do: functional testing starts as pre-functional finishes.",
    areaAware: false,
    build: () => [
      ...chain("cx", [
        { ref: "prefunc", name: "Pre-functional checks", durationDays: 10, category: "prefunctional" },
        { ref: "func", name: "Functional testing", durationDays: 15, category: "functional" },
        { ref: "ist", name: "Integrated systems test (IST)", durationDays: 5, category: "ist" },
        { ref: "istdone", name: "IST complete", type: "finish_ms", durationDays: 0, kind: "ist_complete", category: "ist" },
        { ref: "punch", name: "Punch list", durationDays: 10, category: "punch", stage: "closeout" },
        { ref: "turnover", name: "Turnover to owner", type: "finish_ms", durationDays: 0, kind: "turnover", category: "turnover", stage: "turnover" },
      ], { stage: "commissioning" }),
      // Training runs alongside the testing rather than after it - it is
      // the one thing that genuinely overlaps, so the template says so.
      row({
        ref: "cx-training", name: "Owner training", durationDays: 5,
        category: "closeout", stage: "closeout",
        after: [{ ref: "cx-func", type: "SS", lagDays: 5 }],
      }),
    ],
  },

  {
    key: "weekly",
    name: "Weekly coordination rhythm",
    description:
      "The meeting cadence and the design gates. Meetings are a cadence, not a network — these stay dated rather than linked.",
    areaAware: false,
    build: ({ anchor } = {}) => [
      row({ ref: "wk-meet", name: "Coordination clash meeting", type: "meeting", durationDays: 0, category: "meeting", stage: "preconstruction", startDate: isoWeeksFrom(anchor, 1), recurrence: { freq: "weekly", until: isoWeeksFrom(anchor, 12) } }),
      ms({ ref: "wk-sd", name: "SD sign-off", kind: "design_release", category: "design", stage: "preconstruction", after: [] , startDate: isoWeeksFrom(anchor, 3) }),
      ms({ ref: "wk-dd", name: "DD sign-off", kind: "design_release", category: "design", stage: "preconstruction", startDate: isoWeeksFrom(anchor, 6) }),
      ms({ ref: "wk-cd", name: "CD sign-off", kind: "design_release", category: "design", stage: "preconstruction", startDate: isoWeeksFrom(anchor, 9) }),
    ],
  },
];

// Turn a template's rows into editor rows. `newRow` is the page's row
// factory and `keyOf` hands back the key it allocated, so `after` refs
// become real predecessor links rather than row numbers.
export function buildTemplateRows(tpl, ctx, newRow) {
  const built = tpl.build(ctx || {}) || [];
  const keyByRef = new Map();
  const rows = built.map((t) => {
    const r = newRow({
      name: t.name,
      type: t.type,
      kind: t.kind || "",
      category: t.category || "",
      stage: t.stage || "",
      indent: t.indent || 0,
      durationDays: t.durationDays || 0,
      level: t.level || "",
      discipline: t.discipline || "",
      // Only rows the template dated keep a pinned start; the rest are
      // driven by their links.
      manualStart: t.startDate || "",
      recurrence: t.recurrence,
      predecessors: [],
    });
    if (t.ref) keyByRef.set(t.ref, r.key);
    return r;
  });
  built.forEach((t, i) => {
    rows[i].predecessors = (t.after || [])
      .map((a) => ({ key: keyByRef.get(a.ref), type: a.type || "FS", lagDays: a.lagDays || 0 }))
      .filter((l) => l.key);
  });
  return rows;
}
